// REMINDER: React Compiler is not compatible with Tanstack Table v8 https://github.com/TanStack/table/issues/5567
'use no memo'

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/custom/table'
import { DataTableFilterApplyButton } from '@/components/data-table/data-table-filter-apply-button'
import { DataTableFilterControls } from '@/components/data-table/data-table-filter-controls'
import { DataTableProvider } from '@/components/data-table/data-table-provider'
import { DataTableToolbar } from '@/components/data-table/data-table-toolbar' // TODO: check where to put this
import type { DataTableFilterField } from '@/components/data-table/types'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { TooltipProvider } from '@/components/ui/tooltip'
import { useHotKey } from '@/hooks/use-hot-key'
import { useLocalStorage } from '@/hooks/use-local-storage'
import { useMountEffect } from '@/hooks/useMountEffect'
import {
  getColumnOrderKey,
  getColumnVisibilityKey,
} from '@/lib/constants/local-storage'
import { ApiError } from '@/lib/api'
import { formatCompactNumber } from '@/lib/format'
import { useFilterState } from '@/lib/store/hooks/useFilterState'
import { arrSome, inDateRange } from '@/lib/table/filterfns'
import { cn } from '@/lib/utils'
import {
  type FetchNextPageOptions,
  type FetchPreviousPageOptions,
  type RefetchOptions,
} from '@tanstack/react-query'
import type {
  ColumnDef,
  ColumnFiltersState,
  Row,
  RowSelectionState,
  SortingState,
  TableOptions,
  Table as TTable,
  VisibilityState,
} from '@tanstack/react-table'
import {
  flexRender,
  getCoreRowModel,
  getFacetedRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  getFacetedMinMaxValues as getTTableFacetedMinMaxValues,
  getFacetedUniqueValues as getTTableFacetedUniqueValues,
  useReactTable,
} from '@tanstack/react-table'
import { LoaderCircle, PanelRight, TriangleAlert } from 'lucide-react'
import * as React from 'react'

// Fractional browser zoom can stop scrollTop short of the exact bottom.
const SCROLL_BOTTOM_THRESHOLD_PX = 100

// TODO: add a possible chartGroupBy
export interface DataTableInfiniteProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[]
  getRowClassName?: (row: Row<TData>) => string
  // REMINDER: make sure to pass the correct id to access the rows
  getRowId?: TableOptions<TData>['getRowId']
  data: TData[]
  defaultColumnFilters?: ColumnFiltersState
  defaultColumnSorting?: SortingState
  defaultRowSelection?: RowSelectionState
  defaultColumnVisibility?: VisibilityState
  filterFields?: DataTableFilterField<TData>[]
  // REMINDER: close to the same signature as the `getFacetedUniqueValues` of the `useReactTable`
  getFacetedUniqueValues?: (
    table: TTable<TData>,
    columnId: string
  ) => Map<string, number>
  getFacetedMinMaxValues?: (
    table: TTable<TData>,
    columnId: string
  ) => undefined | [number, number]
  totalRows?: number
  totalRowsFetched?: number
  isFetching?: boolean
  isLoading?: boolean
  hasNextPage?: boolean
  fetchNextPage: (
    options?: FetchNextPageOptions | undefined
  ) => Promise<unknown>
  fetchPreviousPage?: (
    options?: FetchPreviousPageOptions | undefined
  ) => Promise<unknown>
  refetch: (options?: RefetchOptions | undefined) => void
  /**
   * React Query's dataUpdatedAt (Unix ms). When provided, rows that appear
   * after the first load are highlighted with animate-highlight-fade for 15 s.
   * Omit to disable highlighting entirely.
   */
  dataUpdatedAt?: number
  renderLiveRow?: (props?: { row: Row<TData> }) => React.ReactNode
  // Used to store column order and visibility in local storage for specific data-table namespace
  tableId?: string
  // Optional slots for extensibility
  sheetSlot?: React.ReactNode
  toolbarActions?: React.ReactNode
  chartSlot?: React.ReactNode
  footerSlot?: React.ReactNode
  /**
   * Alternate presentation of the table's data (e.g. a calendar view).
   * While set, it renders in the table's place — same toolbar, filter rail,
   * and query results — and the table stays mounted but hidden so column
   * sizing/scroll state survive toggling between views.
   */
  contentSlot?: React.ReactNode
  /**
   * When false, rows are not selectable and the "See details" hover affordance
   * is hidden — for tables that have no details sidesheet. Default true.
   */
  enableRowDetails?: boolean
  /**
   * Click anywhere on a row. A pointer convenience only — it adds no selection,
   * no trailing overlay cell and no hint, so it costs a table none of the
   * layout that `enableRowDetails` brings with it. Clicks originating in an
   * interactive element (a kebab, a link) are ignored.
   *
   * Keyboard access is deliberately not implied: a row is not a control, and
   * making it one prunes its contents from the accessibility tree. Give the
   * cell its own button and this stays the mouse shortcut for it.
   */
  onRowClick?: (row: TData) => void
  /**
   * Server-driven mode. When true, TanStack does NOT re-apply column filters
   * client-side — the parent has already filtered via the API (relay). Required
   * for correct server-side filtering on infinite tables: otherwise the loaded
   * page is filtered a SECOND time client-side against raw cell values, which
   * silently drops every row whose type differs from the filter value (e.g.
   * boolean `is_billable` vs string "false", or a date-string column vs a
   * `Date` range) — leaving an empty table despite valid API rows.
   */
  manualFiltering?: boolean
  /**
   * The sorting counterpart to `manualFiltering`: when true, TanStack renders
   * the page in the order the server returned it instead of re-sorting it
   * client-side. Tables whose sort is served by the API (DataTableStoreSync
   * pushes `sort` to the query) should set this, otherwise the client
   * comparator runs over the already-ordered page and can disagree with the
   * server's `ORDER BY` — most visibly on join-backed columns like `duty_name`
   * / `patient_name`, whose cell value isn't what the server sorted on.
   */
  manualSorting?: boolean
  /**
   * True while react-query is showing the PREVIOUS page's rows as placeholder
   * during a filter/sort-change refetch (`isPlaceholderData`). Dims the table
   * body so the user sees the view is updating instead of it looking frozen.
   * Not set during scroll-to-load-next-page, so it won't flicker on scroll.
   */
  isRefetching?: boolean
  /**
   * Filter-induced error state, rendered INSIDE the table body so the toolbar +
   * filter rail stay mounted (the user can always reach the filter to fix it).
   * A recoverable 4xx `error` (an `ApiError` carrying the status) → "invalid
   * filter" copy + a Clear-filters action; anything else → generic "couldn't
   * load" + Retry. `onClearFilters` should reset the filter store (the consumer
   * passes `useFilterActions().resetAllFilters`).
   */
  isError?: boolean
  error?: unknown
  entityLabel?: string
  onClearFilters?: () => void
  /**
   * When true, columns get content-fit initial widths: the table first renders
   * in `table-layout: auto` (columns size to their content), then those measured
   * widths are frozen as the column sizing and the table switches to fixed
   * layout — so the content-fit look is the default AND columns stay resizable.
   * Widths are measured once from the first loaded page. Default false.
   */
  autoColumnWidth?: boolean
  /**
   * Column left at `width: auto` once the others freeze, so it absorbs the
   * slack and the table always fills its container.
   *
   * Without one, `autoColumnWidth` freezes every column to an explicit px
   * width and `table-layout: fixed` has nothing left to distribute — a table
   * whose columns measure narrower than the panel renders with a dead strip to
   * the right of the last column. Tables whose columns already overflow (Call
   * Logs) never show that, which is why this only needs setting on the narrow
   * ones. Name the column that should grow, usually the primary text one.
   */
  flexColumnId?: string
  /**
   * Size the rail and table to this element rather than to the viewport, for
   * a host that gives the table a box of its own height (one that can sit
   * partly below the fold in a scrolling page).
   */
  hostRef?: React.RefObject<HTMLElement | null>
  /** Opens the filter rail on mount without saving it as the user's choice;
   *  unset, the saved choice applies. */
  initialFiltersOpen?: boolean
  /** Replaces "No results." once a query comes back empty. */
  emptyState?: React.ReactNode
  /** A row under the toolbar's buttons. */
  toolbarFilters?: React.ReactNode
}

export function DataTableInfinite<TData, TValue>({
  columns,
  getRowClassName,
  getRowId,
  data,
  defaultColumnFilters = [],
  defaultColumnSorting = [],
  defaultRowSelection = {},
  defaultColumnVisibility = {},
  filterFields = [],
  isFetching,
  isLoading,
  fetchNextPage,
  hasNextPage,
  fetchPreviousPage: _fetchPreviousPage,
  refetch,
  dataUpdatedAt,
  totalRows,
  totalRowsFetched,
  getFacetedUniqueValues,
  getFacetedMinMaxValues,
  renderLiveRow,
  tableId = 'infinite',
  sheetSlot,
  toolbarActions,
  chartSlot,
  footerSlot,
  contentSlot,
  enableRowDetails = true,
  onRowClick,
  manualFiltering = false,
  manualSorting = false,
  isRefetching = false,
  isError = false,
  error,
  entityLabel = 'data',
  onClearFilters,
  autoColumnWidth = false,
  flexColumnId,
  hostRef,
  initialFiltersOpen,
  emptyState,
  toolbarFilters,
}: DataTableInfiniteProps<TData, TValue>) {
  const [columnFilters, setColumnFilters] =
    React.useState<ColumnFiltersState>(defaultColumnFilters)
  const [sorting, setSorting] =
    React.useState<SortingState>(defaultColumnSorting)
  const [rowSelection, setRowSelection] =
    React.useState<RowSelectionState>(defaultRowSelection)
  const [columnOrder, setColumnOrder] = useLocalStorage<string[]>(
    getColumnOrderKey(tableId),
    []
  )
  const [columnVisibility, setColumnVisibility] =
    useLocalStorage<VisibilityState>(
      getColumnVisibilityKey(tableId),
      defaultColumnVisibility
    )
  // Filter-only columns hold a sidebar filter's value and are never shown,
  // whatever visibility the table saved before they existed.
  const tableColumnVisibility = React.useMemo<VisibilityState>(() => {
    const hidden = columns
      .filter((column) => column.meta?.filterOnly && column.id)
      .map((column) => [column.id as string, false])
    return hidden.length > 0
      ? { ...columnVisibility, ...Object.fromEntries(hidden) }
      : columnVisibility
  }, [columns, columnVisibility])
  const topBarRef = React.useRef<HTMLDivElement>(null)
  const tableWrapperRef = React.useRef<HTMLDivElement>(null)
  const [topBarHeight, setTopBarHeight] = React.useState(0)
  // Measured distance from the top of the viewport to the table scroll container.
  // Accounts for app chrome (nav, tab strip) that topBarHeight alone doesn't capture.
  const [tableTopOffset, setTableTopOffset] = React.useState(0)
  // Measured gap between the bottom of the content region (<main>) and the
  // viewport bottom. The content card sits inside a `my-3` wrapper (and can have
  // other app-shell bottom chrome), so it ends above the viewport. Without this,
  // the `100dvh`-based rail/table height overshoots the card and its
  // `overflow: hidden` clips the last 12px — cutting the Search footer's bottom
  // margin so it no longer matches its sides.
  const [tableBottomOffset, setTableBottomOffset] = React.useState(0)

  const onScroll = React.useCallback(
    (e: React.UIEvent<HTMLElement>) => {
      const { scrollHeight, scrollTop, clientHeight } = e.currentTarget
      const onPageBottom =
        scrollHeight - scrollTop - clientHeight <= SCROLL_BOTTOM_THRESHOLD_PX

      if (onPageBottom && !isFetching && !isError && hasNextPage) {
        fetchNextPage()
      }
    },
    [fetchNextPage, isFetching, isError, hasNextPage]
  )

  useMountEffect(() => {
    const measure = () => {
      const topBarRect = topBarRef.current?.getBoundingClientRect()
      if (topBarRect) setTopBarHeight(topBarRect.height)

      const wrapperRect = tableWrapperRef.current?.getBoundingClientRect()
      // Scroll-independent document offset of the table/rail row (constant
      // regardless of scroll position), used to size the sticky rail and table
      // to the space below the app chrome.
      if (wrapperRect) setTableTopOffset(wrapperRect.top + window.scrollY)

      // Distance from the content region's bottom to the viewport bottom. The
      // rail/table live inside this <main>; sizing them to the viewport (100dvh)
      // instead of main's bottom lets them overshoot the clipping card below.
      const mainRect = tableWrapperRef.current
        ?.closest('main')
        ?.getBoundingClientRect()
      // A host below the fold gives a negative offset; with the top offset
      // measured at the same moment, the two still add up to the host's height.
      const hostRect = hostRef?.current?.getBoundingClientRect()
      if (hostRect) setTableBottomOffset(window.innerHeight - hostRect.bottom)
      else if (mainRect)
        setTableBottomOffset(Math.max(0, window.innerHeight - mainRect.bottom))
    }

    // getBoundingClientRect().top is a layout position, which a ResizeObserver
    // (fires on size changes) misses — and the table wrapper is a fixed-height
    // scroll container, so loading rows never resizes it. Without an explicit
    // measure the offset stays at its initial 0 and the sticky rail / table size
    // to the full viewport instead of the space below the app chrome. Measure
    // after first paint and again once the app shell has settled, then keep it
    // fresh on resize.
    const raf = requestAnimationFrame(measure)
    const settleTimer = setTimeout(measure, 500)
    const observer = new ResizeObserver(measure)

    const topBar = topBarRef.current
    const wrapper = tableWrapperRef.current
    const main = wrapper?.closest('main')
    // Content a host stacks above the table moves it without resizing anything
    // observed here, but it does resize the host.
    const host = hostRef?.current
    if (host) observer.observe(host)
    if (topBar) observer.observe(topBar)
    if (wrapper) observer.observe(wrapper)
    if (main) observer.observe(main)
    window.addEventListener('resize', measure)
    return () => {
      cancelAnimationFrame(raf)
      clearTimeout(settleTimer)
      observer.disconnect()
      window.removeEventListener('resize', measure)
    }
  })

  // New-row highlight detection (no useEffect / no setTimeout).
  // Tracks which dataUpdatedAt snapshot each row was first seen at.
  const firstSeenAtRef = React.useRef<Map<string, number>>(new Map())
  const initialDataUpdatedAtRef = React.useRef<number | null>(null)
  const prevDataRef = React.useRef<TData[] | null>(null)

  // Synchronous ref mutations during render — React's "previous value" pattern.
  const _updatedAt = dataUpdatedAt ?? 0
  if (initialDataUpdatedAtRef.current === null && _updatedAt) {
    initialDataUpdatedAtRef.current = _updatedAt
  }
  if (prevDataRef.current !== data) {
    prevDataRef.current = data
    data.forEach((item, index) => {
      // Mirror TanStack Table's ID resolution so stored keys match row.id
      const id = getRowId ? getRowId(item, index, undefined) : String(index)
      if (!firstSeenAtRef.current.has(id)) {
        firstSeenAtRef.current.set(id, _updatedAt)
      }
    })
  }

  const table = useReactTable({
    data,
    columns,
    state: {
      columnFilters,
      sorting,
      columnVisibility: tableColumnVisibility,
      rowSelection,
      columnOrder,
    },
    enableMultiRowSelection: false,
    columnResizeMode: 'onChange',
    // Server-driven tables (e.g. Call Logs) pass manualFiltering so the draft in
    // `columnFilters` only hits the query when the Search button applies it (see
    // DataTableStoreSync). Otherwise getFilteredRowModel would filter the loaded
    // rows live on every keystroke — an on-type "auto search" contradicting
    // apply-on-click.
    manualFiltering,
    // Likewise, server-sorted tables pass manualSorting so the page renders in
    // the server's order rather than being re-sorted by getSortedRowModel.
    manualSorting,
    getRowId,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnFiltersChange: setColumnFilters,
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting,
    onColumnOrderChange: setColumnOrder,
    getSortedRowModel: getSortedRowModel(),
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getTTableFacetedUniqueValues(),
    getFacetedMinMaxValues: getTTableFacetedMinMaxValues(),
    filterFns: { inDateRange, arrSome },
    meta: {
      getRowClassName: (row: Row<TData>) => {
        const seenAt = firstSeenAtRef.current.get(row.id)
        const isNew =
          seenAt !== undefined &&
          seenAt === _updatedAt &&
          _updatedAt !== initialDataUpdatedAtRef.current
        return cn(isNew ? 'animate-highlight-fade' : '', getRowClassName?.(row))
      },
    },
  })

  // NOTE: Filter, sort, and selection syncing is now handled by DataTableStoreSync

  /**
   * https://tanstack.com/table/v8/docs/guide/column-sizing#advanced-column-resizing-performance
   * Instead of calling `column.getSize()` on every render for every header
   * and especially every data cell (very expensive),
   * we will calculate all column sizes at once at the root table level in a useMemo
   * and pass the column sizes down as CSS variables to the <table> element.
   */
  const tableState = table.getState()
  // Content-fit-then-freeze: with autoColumnWidth on, the table first renders in
  // table-layout:auto (columns size to content); a layout effect then captures
  // those widths as the column sizing and freezes to fixed layout, so the
  // content-fit look is preserved AND columns stay resizable. Until frozen, the
  // width vars are `auto` so the browser owns the widths.
  const [autoWidthFrozen, setAutoWidthFrozen] = React.useState(false)
  const autoFitting = autoColumnWidth && !autoWidthFrozen
  // Keyed on the header ids as well as the sizing state, because the set of
  // columns can change after the first paint — a column that only exists once
  // its data arrives is the common case. Without this the memo never re-runs
  // for it, no `--col-<id>-size` is emitted, and its cells resolve
  // `width: var(--col-<id>-size)` to nothing: under `table-layout: fixed` that
  // column then absorbs every pixel the others do not claim.
  //
  // The other sizing deps are stable references, so this cannot be left to
  // them. It also does not reproduce under jsdom, where the visibility default
  // is a fresh object each render and the memo happens to re-run anyway.
  const headerIds = table
    .getFlatHeaders()
    .map((header) => header.id)
    .join(',')
  const columnSizeVars = React.useMemo(() => {
    const headers = table.getFlatHeaders()
    const colSizes: { [key: string]: string } = {}
    for (let i = 0; i < headers.length; i++) {
      const header = headers[i]!
      // REMINDER: replace "." with "-" to avoid invalid CSS variable name (e.g. "timing.dns" -> "timing-dns")
      // The flex column stays `auto` after the freeze too, which is what makes
      // it take the leftover width rather than sitting at its measured size.
      const flexes = header.column.id === flexColumnId
      colSizes[`--header-${header.id.replace('.', '-')}-size`] =
        autoFitting || flexes ? 'auto' : `${header.getSize()}px`
      colSizes[`--col-${header.column.id.replace('.', '-')}-size`] =
        autoFitting || flexes ? 'auto' : `${header.column.getSize()}px`
    }
    return colSizes
    // eslint-disable-next-line react-hooks/exhaustive-deps -- openstatus registry
  }, [
    tableState.columnSizingInfo,
    tableState.columnSizing,
    tableState.columnVisibility,
    autoFitting,
    flexColumnId,
    headerIds,
  ])

  // Measure the content-fit column widths once (while in auto layout), write
  // them as the column sizing, and freeze to fixed layout — preserving the fit
  // while making columns resizable from there.
  React.useLayoutEffect(() => {
    if (!autoFitting || data.length === 0) return
    const container = tableWrapperRef.current
    if (!container) return
    const cells = container.querySelectorAll<HTMLElement>('th[data-column-id]')
    if (cells.length === 0) return
    const sizes: Record<string, number> = {}
    cells.forEach((cell) => {
      const id = cell.dataset.columnId
      if (id) sizes[id] = Math.floor(cell.getBoundingClientRect().width)
    })
    if (Object.keys(sizes).length === 0) return

    table.setColumnSizing(sizes)
    setAutoWidthFrozen(true)
  }, [autoFitting, data.length, table])

  useHotKey(() => {
    setColumnOrder([])
    setColumnVisibility(defaultColumnVisibility)
  }, 'u')

  // Memoize column state as strings for efficient row memoization comparison
  const visibleColumnIds = React.useMemo(
    () =>
      table
        .getVisibleLeafColumns()
        .map((c) => c.id)
        .join(','),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- openstatus registry
    [table.getState().columnVisibility]
  )
  const columnOrderString = React.useMemo(
    () => columnOrder.join(','),
    [columnOrder]
  )

  // Initial load with no rows yet: the empty-state cell already shows a
  // spinner, so the footer row renders nothing extra. Otherwise the footer
  // shows a row-count summary (plus a small spinner while the next page
  // auto-loads on scroll — see onScroll).
  const hasRows = (table.getRowModel().rows?.length ?? 0) > 0
  const isInitialLoad = !hasRows && (isLoading || isFetching)
  // Only a validation status (400/422) is a filter the user can fix by clearing
  // it; auth/not-found/rate-limit (401/403/404/429) and 5xx/network are not.
  const errorStatus = error instanceof ApiError ? error.status : undefined
  const isBadFilter = errorStatus === 400 || errorStatus === 422
  // Clear resets BOTH sources of filter state: TanStack `columnFilters` (drives
  // the rail inputs, which read the table state) and, via `onClearFilters`, the
  // nuqs store/URL that feeds the API query. Resetting only the store left the
  // visible controls populated.
  const handleClearFilters = React.useCallback(() => {
    table.resetColumnFilters()
    onClearFilters?.()
  }, [table, onClearFilters])
  // Tables with no filter fields (e.g. the v3 data tables) get no filter rail —
  // rendering it leaves an empty column that pushes the table over.
  const hasFilterRail = filterFields.length > 0 || Boolean(footerSlot)

  // Auto-load succeeds only while the container can scroll, so onScroll fires.
  // When a loaded page doesn't fill it (few filtered rows, a tall display, or a
  // small page size), keep fetching until the container overflows or the cursor
  // drains — otherwise the user is stranded with more data and no way to reach it.
  const rowCount = table.getRowModel().rows.length
  // A zero-height container (display:none via contentSlot, an inactive
  // forceMount tab, …) measures scrollHeight === clientHeight === 0, which
  // this heuristic would read as "not filled" — draining every remaining
  // page in the background. Guard on the measurement itself; hasContentSlot
  // stays in the deps so the effect re-arms when the table becomes visible.
  const hasContentSlot = Boolean(contentSlot)
  React.useEffect(() => {
    const container = tableWrapperRef.current
    if (!container) return
    if (container.clientHeight === 0) return
    if (
      container.scrollHeight <= container.clientHeight &&
      hasNextPage &&
      !isFetching &&
      !isError
    ) {
      fetchNextPage()
    }
  }, [
    rowCount,
    hasContentSlot,
    hasNextPage,
    isFetching,
    isError,
    fetchNextPage,
  ])

  return (
    <DataTableProvider
      table={table}
      columns={columns}
      filterFields={filterFields}
      columnFilters={columnFilters}
      sorting={sorting}
      rowSelection={rowSelection}
      columnOrder={columnOrder}
      columnVisibility={columnVisibility}
      enableColumnOrdering={true}
      isLoading={isFetching || isLoading}
      totalRows={totalRows}
      getFacetedUniqueValues={getFacetedUniqueValues}
      getFacetedMinMaxValues={getFacetedMinMaxValues}
      initialControlsOpen={initialFiltersOpen}>
      {/* Single shared TooltipProvider for the whole table tree (cells, the
          generic data-table sheet, and the feature sheetSlot) so per-cell
          tooltips share one provider's delay/skip-delay grouping instead of
          each TextWithTooltip / CallLabelBadge mounting its own. */}
      <TooltipProvider delayDuration={100} disableHoverableContent>
        <div
          className="flex h-full min-h-screen w-full flex-col"
          style={
            {
              '--top-bar-height': `${topBarHeight}px`,
              '--table-top-offset': `${tableTopOffset}px`,
              '--table-bottom-offset': `${tableBottomOffset}px`,
              ...columnSizeVars,
            } as React.CSSProperties
          }>
          {/* Full-width toolbar: stays above both rail + table so the "Hide Filters"
            toggle has a stable position whether the rail is open or collapsed. */}
          <div
            ref={topBarRef}
            className={cn('sticky top-0 z-30 flex flex-col gap-2 py-3')}>
            <DataTableToolbar
              renderActions={toolbarActions ? () => toolbarActions : undefined}
              filters={toolbarFilters}
            />
            {chartSlot}
          </div>
          <div className="flex flex-1 flex-col sm:flex-row">
            {hasFilterRail ? (
              <div
                className={cn(
                  'h-full w-full flex-col sm:sticky sm:top-[var(--top-bar-height)] sm:max-h-[calc(100dvh-var(--table-top-offset)-var(--table-bottom-offset))] sm:min-h-0 sm:self-start',
                  'hidden sm:flex',
                  // Slide + fade between 0 and the rail width; inner content keeps
                  // a fixed width so it clips instead of reflowing mid-animation.
                  // visibility (animated discretely: flips at the end of collapse,
                  // instantly on expand) keeps the clipped rail's fields out of
                  // the tab order and a11y tree, like the previous `hidden` did.
                  'sm:overflow-hidden sm:transition-[width,opacity,visibility] sm:ease-out',
                  'sm:group-data-[expanded=true]/controls:w-52 md:group-data-[expanded=true]/controls:w-72',
                  'sm:group-data-[expanded=false]/controls:w-0 sm:group-data-[expanded=false]/controls:opacity-0 sm:group-data-[expanded=false]/controls:invisible'
                )}>
                <div className="scrollbar-hidden flex-1 px-3 pb-3 sm:w-52 sm:min-h-0 sm:overflow-y-auto md:w-72">
                  <DataTableFilterControls />
                </div>
                {filterFields.length > 0 ? (
                  <div className="border-neutral-subtle-border bg-background/40 border-t p-4 sm:w-52 sm:shrink-0 md:w-72">
                    <DataTableFilterApplyButton />
                  </div>
                ) : null}
                {footerSlot ? (
                  <div className="border-neutral-subtle-border bg-background/40 border-t p-4 sm:w-52 sm:shrink-0 md:w-72">
                    {footerSlot}
                  </div>
                ) : null}
              </div>
            ) : null}
            <div
              className={cn(
                // min-w-0 lets this flex child shrink below its content size so the
                // inner overflow-x-auto container owns horizontal scroll. Using a
                // viewport-based max-width here breaks when the chat sidebar narrows
                // the content panel (table overflows past the panel and can't scroll).
                'flex min-w-0 max-w-full flex-1 flex-col',
                // Divider between the filter rail and the table — only when the
                // rail is present.
                hasFilterRail && 'border-neutral-subtle-border sm:border-l'
              )}>
              {contentSlot ? (
                <div className="bg-background min-h-0 max-h-[calc(100dvh-var(--table-top-offset)-var(--table-bottom-offset))] flex-1 overflow-auto p-3">
                  {contentSlot}
                </div>
              ) : null}
              <Table
                containerRef={tableWrapperRef}
                onScroll={onScroll}
                // REMINDER: https://stackoverflow.com/questions/50361698/border-style-do-not-work-with-sticky-position-element
                className="border-separate border-spacing-0"
                style={
                  autoFitting
                    ? { tableLayout: 'auto' }
                    : { minWidth: table.getTotalSize(), tableLayout: 'fixed' }
                }
                containerClassName={cn(
                  'bg-background flex-1 min-h-0 max-h-[calc(100dvh-var(--table-top-offset)-var(--table-bottom-offset))]',
                  // Keep the table mounted (column sizing/scroll state) while an
                  // alternate view is shown in its place.
                  contentSlot && 'hidden'
                )}>
                <TableHeader
                  className={cn(
                    'bg-white/55 sticky top-0 z-20 backdrop-blur-sm'
                  )}>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <TableRow
                      key={headerGroup.id}
                      className={cn(
                        'bg-transparent hover:bg-transparent',
                        'border-neutral-subtle-border'
                      )}>
                      {headerGroup.headers.map((header) => {
                        return (
                          <TableHead
                            key={header.id}
                            data-column-id={header.column.id}
                            style={
                              header.column.getCanResize()
                                ? {
                                    width: `var(--header-${header.id.replace('.', '-')}-size)`,
                                    minWidth: `var(--header-${header.id.replace('.', '-')}-size)`,
                                  }
                                : undefined
                            }
                            className={cn(
                              // border-y gives the sticky header (and thus the
                              // table) a visible top stroke; with border-separate
                              // a border on the <table> itself wouldn't render on
                              // the sticky header. border-b keeps the header divider.
                              // border-r adds the vertical column line (dropped on
                              // the last column so it doesn't double the edge).
                              'border-neutral-subtle-border relative truncate border-y border-r select-none last:border-r-0 last:[&>.cursor-col-resize]:opacity-0',
                              header.column.columnDef.meta?.headerClassName
                            )}
                            aria-sort={
                              header.column.getIsSorted() === 'asc'
                                ? 'ascending'
                                : header.column.getIsSorted() === 'desc'
                                  ? 'descending'
                                  : 'none'
                            }>
                            {header.isPlaceholder
                              ? null
                              : flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                            {header.column.getCanResize() && (
                              <div
                                onDoubleClick={() => header.column.resetSize()}
                                onMouseDown={header.getResizeHandler()}
                                onTouchStart={header.getResizeHandler()}
                                className={cn(
                                  'group/resizer user-select-none absolute top-0 -right-2 z-10 flex h-full w-4 cursor-col-resize touch-none justify-center',
                                  'before:bg-neutral-subtle-border before:absolute before:inset-y-0 before:w-px before:translate-x-px before:transition-colors',
                                  'hover:before:bg-primary-solid'
                                )}
                              />
                            )}
                          </TableHead>
                        )
                      })}
                      {/* Zero-width spacer matching the body rows' "See details"
                          overlay cell. Under table-layout:fixed the column count
                          and widths come from the first (header) row, so without
                          this the overlay column is treated as auto and steals the
                          slack — stopping the data columns from filling the table
                          width as they did before the overlay existed. */}
                      <th aria-hidden className="w-0 border-0 p-0" />
                    </TableRow>
                  ))}
                </TableHeader>
                <TableBody
                  id="content"
                  tabIndex={-1}
                  aria-busy={isRefetching}
                  className={cn(
                    'outline-primary outline-1 -outline-offset-1 outline-none transition-opacity focus-visible:outline-solid',
                    isRefetching && 'pointer-events-none opacity-50'
                  )}
                  // REMINDER: avoids scroll (skipping the table header) when using skip to content
                  style={{
                    scrollMarginTop: 'calc(var(--top-bar-height) + 40px)',
                  }}>
                  {isError ? (
                    <TableRow className="hover:bg-transparent data-[state=selected]:bg-transparent">
                      <TableCell colSpan={columns.length} className="h-64">
                        <div className="mx-auto flex max-w-md flex-col items-center gap-3 py-8 text-center">
                          <Alert
                            variant={isBadFilter ? 'default' : 'destructive'}
                            className="text-left">
                            <TriangleAlert className="h-4 w-4" />
                            <AlertTitle>
                              {isBadFilter
                                ? "This filter isn't valid"
                                : `Couldn't load ${entityLabel}`}
                            </AlertTitle>
                            <AlertDescription>
                              {isBadFilter && error instanceof Error
                                ? error.message
                                : 'Something went wrong on our end. Please try again.'}
                            </AlertDescription>
                          </Alert>
                          <div className="flex gap-2">
                            {!isBadFilter ? (
                              <Button size="sm" onClick={() => refetch()}>
                                Retry
                              </Button>
                            ) : null}
                            {onClearFilters ? (
                              <Button
                                size="sm"
                                variant={isBadFilter ? 'default' : 'outline'}
                                onClick={handleClearFilters}>
                                Clear filters
                              </Button>
                            ) : null}
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : table.getRowModel().rows?.length ? (
                    table.getRowModel().rows.map((row) => (
                      // REMINDER: if we want to add arrow navigation https://github.com/TanStack/table/discussions/2752#discussioncomment-192558
                      <React.Fragment key={row.id}>
                        {renderLiveRow?.({ row })}
                        <MemoizedRow
                          row={row}
                          table={table}
                          selected={row.getIsSelected()}
                          visibleColumnIds={visibleColumnIds}
                          columnOrder={columnOrderString}
                          enableRowDetails={enableRowDetails}
                          onRowClick={onRowClick}
                        />
                      </React.Fragment>
                    ))
                  ) : isInitialLoad ? (
                    // First load with no rows yet — show a loading state instead
                    // of "No results." (which reads as "empty" before data lands).
                    <TableRow className="hover:bg-transparent data-[state=selected]:bg-transparent">
                      <TableCell
                        colSpan={columns.length}
                        className="h-24 text-center">
                        <div className="text-muted-foreground flex items-center justify-center gap-2">
                          <LoaderCircle className="h-4 w-4 animate-spin-gpu" />
                          <span className="text-sm">
                            Loading {entityLabel}…
                          </span>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : (
                    <React.Fragment>
                      {renderLiveRow?.()}
                      <TableRow>
                        <TableCell
                          colSpan={columns.length}
                          className="h-24 text-center">
                          {/* Still fetching (initial load or a filter re-query):
                              show a spinner, not "No results" — the query hasn't
                              resolved yet, so we don't know there's nothing. */}
                          {isLoading || isFetching ? (
                            <div className="flex items-center justify-center gap-2">
                              <LoaderCircle className="text-muted-foreground h-4 w-4 animate-spin-gpu" />
                              <span className="text-muted-foreground text-sm">
                                Searching…
                              </span>
                            </div>
                          ) : emptyState ? (
                            // Centred in the visible width, not the full
                            // (horizontally scrolling) table width.
                            <div className="sticky left-1/2 w-fit -translate-x-1/2">
                              {emptyState}
                            </div>
                          ) : (
                            'No results.'
                          )}
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  )}
                  <TableRow className="hover:bg-transparent data-[state=selected]:bg-transparent">
                    <TableCell colSpan={columns.length} className="text-center">
                      {/* When there are no rows, the body already shows the
                        "No results." empty state — suppress the footer entirely
                        rather than printing a redundant "0 of 0 rows". */}
                      {isError || isInitialLoad || !hasRows ? null : (
                        <div className="sticky left-1/2 flex w-fit -translate-x-1/2 items-center gap-2">
                          {isFetching ? (
                            <LoaderCircle className="text-muted-foreground h-4 w-4 animate-spin-gpu" />
                          ) : null}
                          <p className="text-muted-foreground text-sm">
                            {totalRows != null ? (
                              <>
                                <span className="font-mono font-medium">
                                  {formatCompactNumber(rowCount)}
                                </span>{' '}
                                of{' '}
                                <span className="font-mono font-medium">
                                  {formatCompactNumber(
                                    Math.max(totalRows, totalRowsFetched ?? 0)
                                  )}
                                </span>{' '}
                                rows
                              </>
                            ) : (
                              <>
                                Showing{' '}
                                <span className="font-mono font-medium">
                                  {formatCompactNumber(rowCount)}
                                </span>{' '}
                                rows
                              </>
                            )}
                          </p>
                          {hasNextPage && !isFetching ? (
                            <Button
                              variant="link"
                              className="text-muted-foreground hover:text-foreground h-auto p-0 font-normal"
                              onClick={() => fetchNextPage()}>
                              Load more
                            </Button>
                          ) : null}
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
        {sheetSlot}
      </TooltipProvider>
    </DataTableProvider>
  )
}

/**
 * REMINDER: this is the heaviest component in the table if lots of rows
 * Some other components are rendered more often necessary, but are fixed size (not like rows that can grow in height)
 * e.g. DataTableFilterControls, DataTableToolbar, DataTableHeader
 */

function Row<TData>({
  row,
  table,
  selected,
  visibleColumnIds: _visibleColumnIds,
  columnOrder: _columnOrder,
  enableRowDetails = true,
  onRowClick,
}: {
  row: Row<TData>
  table: TTable<TData>
  // REMINDER: row.getIsSelected(); - just for memoization
  selected?: boolean
  // REMINDER: for memoization - triggers re-render when columns change
  visibleColumnIds: string
  columnOrder: string
  // When false, the row is not selectable and shows no "See details" overlay.
  enableRowDetails?: boolean
  // Pointer-only row click — see the prop's note on DataTableInfiniteProps.
  onRowClick?: (row: TData) => void
}) {
  // REMINDER: rerender the row when live mode is toggled - used to opacity the row
  // via the `getRowClassName` prop - but for some reasons it wil render the row on data fetch
  useFilterState((s) => s.live)
  // Selection when the table owns row details, otherwise the caller's plain
  // click handler. Never both: a table with `enableRowDetails` already opens
  // its own sheet from the selection.
  const activate = enableRowDetails
    ? () => row.toggleSelected()
    : onRowClick
      ? () => onRowClick(row.original)
      : null
  return (
    <TableRow
      id={row.id}
      tabIndex={enableRowDetails ? 0 : undefined}
      data-state={selected && 'selected'}
      onClick={
        activate
          ? (e) => {
              // Don't select the row when clicking interactive elements inside
              // cells (action buttons, copy buttons, links, etc.)
              if (
                (e.target as HTMLElement).closest(
                  'button, a, input, [role="button"]'
                )
              )
                return
              activate()
            }
          : undefined
      }
      onKeyDown={
        enableRowDetails
          ? (event) => {
              if (event.key === 'Enter') {
                event.preventDefault()
                row.toggleSelected()
              }
            }
          : undefined
      }
      className={cn(
        // Neutral-gray row hover/focus (per design) instead of the indigo-tinted
        // shared TableRow default, which reads as too faint/colored on a wide,
        // many-column table. Overrides the shared default via cn/twMerge.
        'group outline-primary-solid outline-1 -outline-offset-1 transition-colors outline-none',
        activate && 'hover:bg-neutral-hover-bg cursor-pointer',
        // Focus and selection styling belong to the selectable variant only: a
        // plain click handler adds no focus target and nothing to select.
        enableRowDetails &&
          'focus-visible:bg-neutral-hover-bg focus-visible:outline-solid data-[state=selected]:outline-solid',
        // Keep the last data column borderless on its right edge (as it was
        // before the overlay existed). With row details on, the trailing
        // "See details" overlay cell is the real last child, so the data column
        // is nth-last-child(2); without it, the data column is the last child.
        enableRowDetails
          ? '[&>td:nth-last-child(2)]:border-r-0'
          : '[&>td:last-child]:border-r-0',
        table.options.meta?.getRowClassName?.(row)
      )}>
      {row.getVisibleCells().map((cell) => (
        <TableCell
          key={cell.id}
          style={
            cell.column.getCanResize()
              ? {
                  width: `var(--col-${cell.column.id.replace('.', '-')}-size)`,
                  minWidth: `var(--col-${cell.column.id.replace('.', '-')}-size)`,
                }
              : undefined
          }
          className={cn(
            // border-r draws the vertical column line (dropped on the last
            // column so it doesn't double the table edge).
            'border-neutral-subtle-border truncate border-b border-r last:border-r-0',
            cell.column.columnDef.meta?.cellClassName
          )}>
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </TableCell>
      ))}
      {/* Hover affordance: a "See details" hint pinned to the row's right edge
          that fades in on hover, so users know a row opens the detail sheet.
          Zero-width cell so it doesn't shift column alignment; the overlay is
          absolutely positioned and clicks fall through to the row. Omitted for
          tables with no details sheet. */}
      {enableRowDetails ? (
        <td aria-hidden className="sticky right-0 z-20 w-0 border-0 p-0">
          <div className="bg-app absolute inset-y-0 right-px flex items-center pr-3 pl-6 opacity-0 transition-opacity group-hover:opacity-100 h-[calc(100%-1px)]">
            <span className="text-primary-high-contrast flex items-center gap-1.5 text-sm font-medium whitespace-nowrap">
              See details
              <PanelRight className="size-4 shrink-0" aria-hidden />
            </span>
          </div>
        </td>
      ) : null}
    </TableRow>
  )
}

const MemoizedRow = React.memo(
  Row,
  (prev, next) =>
    prev.row.id === next.row.id &&
    prev.selected === next.selected &&
    prev.visibleColumnIds === next.visibleColumnIds &&
    prev.columnOrder === next.columnOrder &&
    prev.enableRowDetails === next.enableRowDetails &&
    prev.onRowClick === next.onRowClick &&
    prev.row.original === next.row.original
) as typeof Row
